import React from 'react';
import '../../../styles/utils/modal/CustomCompleteModal.scss';
import useAppTranslation from '../../../hooks/useAppTranslation';

export default function CustomCompleteModal({ onStart, onClose, data }) {
    const { t } = useAppTranslation('components/utils/customCompleteModal');
    const {
        storyTitle,
        gender,
        name,
        drawType,
        selectedOptions,
        type,
        userImage,
    } = data;

    const getDisplayValue = React.useCallback(
        (key, value) => {
            if (!value || value === '선택 안됨') return t('values.notSelected');

            if (key === 'gender') {
                if (value === '남자') return t('values.gender.male');
                if (value === '여자') return t('values.gender.female');
            }

            if (key === 'type') {
                if (value === '그림 동화') return t('values.type.pictureBook');
                if (value === '그림 소설') return t('values.type.graphicNovel');
            }

            return value;
        },
        [t]
    );

    const summaryItems = React.useMemo(
        () => [
            {
                label: t('summary.storyTitle'),
                value: getDisplayValue('storyTitle', storyTitle),
            },
            {
                label: t('summary.gender'),
                value: getDisplayValue('gender', gender),
            },
            { label: t('summary.name'), value: getDisplayValue('name', name) },
            {
                label: t('summary.drawType'),
                value: getDisplayValue('drawType', drawType),
            },
            { label: t('summary.type'), value: getDisplayValue('type', type) },
        ],
        [storyTitle, gender, drawType, getDisplayValue, name, t, type]
    );

    const additionalOptions = React.useMemo(
        () => selectedOptions.filter((opt) => opt && opt.trim() !== ''),
        [selectedOptions]
    );

    const imageSrc = React.useMemo(() => {
        if (!userImage) return null;
        if (typeof userImage === 'string') return userImage;
        if (userImage instanceof Blob || userImage instanceof File) {
            try {
                return URL.createObjectURL(userImage);
            } catch (err) {
                console.error('Failed to create object URL:', err);
                return null;
            }
        }
        return null;
    }, [userImage]);

    React.useEffect(() => {
        return () => {
            if (
                imageSrc &&
                typeof imageSrc === 'string' &&
                imageSrc.startsWith('blob:')
            ) {
                URL.revokeObjectURL(imageSrc);
            }
        };
    }, [imageSrc]);

    return (
        <div className="custom-complete-modal-overlay">
            <div className="custom-complete-modal">
                <button
                    className="close-btn"
                    onClick={onClose}
                >
                    <img
                        src="/images/utils/modal/customCompleteModal/close.svg"
                        alt={t('alts.close')}
                    />
                </button>

                <div className="modal-header">
                    <h2>{t('title')}</h2>
                    <p>{t('description')}</p>
                </div>

                <div className="divider" />

                <div className="modal-body">
                    <div className="section-left">
                        <div className="section-label">
                            {t('sections.uploadedPhoto')}
                        </div>
                        <div className="image-container">
                            {imageSrc ? (
                                <img
                                    src={imageSrc}
                                    alt={t('alts.uploadedPhoto')}
                                />
                            ) : (
                                <div className="no-image">
                                    {t('values.noImage')}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="section-right">
                        <div className="section-label">
                            {t('sections.selectedOptions')}
                        </div>
                        <div className="options-scroll-area">
                            <div className="options-list">
                                {summaryItems.map((item, idx) => (
                                    <div
                                        key={idx}
                                        className="summary-card"
                                    >
                                        <span className="label">
                                            {item.label}
                                        </span>
                                        <div className="card-divider" />
                                        <p className="value">{item.value}</p>
                                    </div>
                                ))}
                                {additionalOptions.map((opt, idx) => (
                                    <div
                                        key={`opt-${idx}`}
                                        className="summary-card"
                                    >
                                        <span className="label">
                                            {t('summary.option', {
                                                number: idx + 1,
                                            })}
                                        </span>
                                        <div className="card-divider" />
                                        <p className="value">{opt}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <button
                    className="btn-start"
                    onClick={onStart}
                >
                    <img
                        src="/images/utils/modal/customCompleteModal/check.svg"
                        alt={t('alts.check')}
                        className="check-icon"
                    />
                    {t('actions.makeStory')}
                </button>
            </div>
        </div>
    );
}
